import { useState } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { OBJECTIVES_EMPTY_SEARCH } from "@/router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { ConfirmRevertDialog } from "@/components/shared/confirm-revert-dialog";
import { StatusBadge } from "@/components/shared/status-badge";
import { StatusTransitionSelect } from "@/components/shared/status-transition-select";
import { ProgressBar } from "@/components/shared/progress-bar";
import { EmptyState } from "@/components/shared/empty-state";
import { KeyResultInlineEditor } from "@/features/key-results/key-result-inline-editor";
import { MoveKeyResultDialog } from "@/features/key-results/move-key-result-dialog";
import { CheckInForm } from "@/features/key-results/check-in-form";
import { ProgressUpdateList } from "@/features/progress-updates/progress-update-list";
import { KudosList } from "@/features/kudos/kudos-list";
import { useKeyResult, useUpdateKeyResultStatus, useDeleteKeyResult } from "@/hooks/use-key-results";
import { useObjective } from "@/hooks/use-objectives";
import { KrCoachPanel } from "@/features/okr-coach/detail-coach-panel";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { useUsers } from "@/hooks/use-users";
import { getEntityPermissions } from "@/lib/permissions";
import { getKrTypeLabel, getQuarterLabel, isTerminalStatus } from "@/lib/status-machine";
import { formatDueDate } from "@/lib/date";
import type { KeyResult, KrStatus } from "@/types/entities";

function KeyResultDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-64" />
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

interface KrHeaderProps {
  keyResult: KeyResult;
  canEdit: boolean;
  canDelete: boolean;
  canMove: boolean;
  canRevertToDraft: boolean;
  onStatusTransition: (status: KrStatus) => void;
  statusPending: boolean;
  onDeleteClick: () => void;
  onMoveClick: () => void;
}

function KrHeader({ keyResult, canEdit, canDelete, canMove, canRevertToDraft, onStatusTransition, statusPending, onDeleteClick, onMoveClick }: KrHeaderProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        <Link
          to="/objectives/$id"
          params={{ id: keyResult.objective_id }}
          className="text-sm text-[hsl(var(--muted-foreground))] hover:underline"
        >
          Objective
        </Link>
        <span className="text-sm text-[hsl(var(--muted-foreground))]">/</span>
        <span className="text-sm">{keyResult.title}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-semibold">{keyResult.title}</h1>
          <StatusBadge status={keyResult.status} />
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <StatusTransitionSelect
              currentStatus={keyResult.status}
              onTransition={onStatusTransition}
              disabled={statusPending}
              canRevertToDraft={canRevertToDraft}
            />
          )}
          {canMove && (
            <Button variant="outline" size="sm" onClick={onMoveClick}>
              Move
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" size="sm" onClick={onDeleteClick}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

function KrProgressCard({ keyResult }: { keyResult: KeyResult }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Progress</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <ProgressBar value={keyResult.completion_percent} className="flex-1" />
          <span className="text-2xl font-semibold tabular-nums">
            {Math.round(keyResult.completion_percent)}%
          </span>
        </div>
        {keyResult.type === "metric" && keyResult.target_value != null && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {keyResult.current_value ?? 0} / {keyResult.target_value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function KrMetadata({ keyResult, year }: { keyResult: KeyResult; year?: number }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Metadata</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Quarter</p>
          <p className="text-sm font-medium">{getQuarterLabel(keyResult.quarter, year)}</p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Due date</p>
          <p className="text-sm font-medium">
            {keyResult.due_date ? formatDueDate(keyResult.due_date) : "—"}
          </p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Type</p>
          <Badge variant="secondary" className="mt-1">{getKrTypeLabel(keyResult.type)}</Badge>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Created</p>
          <p className="text-sm">{new Date(keyResult.created_at).toLocaleDateString()}</p>
        </div>
        <Separator />
        <div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Updated</p>
          <p className="text-sm">{new Date(keyResult.updated_at).toLocaleDateString()}</p>
        </div>
      </CardContent>
    </Card>
  );
}


export function KeyResultDetailPage() {
  const { id } = useParams({ from: "/key-results/$id" });
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { data, isLoading, error } = useKeyResult(id);
  const parentObjectiveId = data?.data.objective_id ?? "";
  const { data: parentObjective } = useObjective(parentObjectiveId);
  const needsHierarchyCheck = isSupervisorOrAbove(currentUser?.role);
  const { data: usersData } = useUsers(needsHierarchyCheck ? { limit: 100 } : { limit: 0 });
  const allUsers = usersData?.data ?? [];
  const updateStatus = useUpdateKeyResultStatus(id);
  const deleteKeyResult = useDeleteKeyResult();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);

  if (isLoading) return <KeyResultDetailSkeleton />;

  if (error || !data) {
    return (
      <EmptyState
        title="Key Result not found"
        description={error?.message ?? "The key result you are looking for does not exist."}
        actionLabel="Back to Objectives"
        onAction={() => void navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH })}
      />
    );
  }

  const keyResult = data.data;
  const { canEdit, canRevertToDraft } = getEntityPermissions({ currentUser, ownerId: keyResult.owner_id, users: allUsers });
  const canDelete = canEdit && keyResult.status === "draft";
  const canMove = canEdit && !isTerminalStatus(keyResult.status);
  const canCheckIn = canEdit && keyResult.status === "in_progress";

  const handleStatusTransition = (status: KrStatus) => {
    if (status === "draft") {
      setRevertDialogOpen(true);
      return;
    }
    updateStatus.mutate(status);
  };

  return (
    <div className="space-y-6">
      <KrHeader
        keyResult={keyResult}
        canEdit={canEdit}
        canDelete={canDelete}
        canMove={canMove}
        canRevertToDraft={canRevertToDraft}
        onStatusTransition={handleStatusTransition}
        statusPending={updateStatus.isPending}
        onDeleteClick={() => setDeleteDialogOpen(true)}
        onMoveClick={() => setMoveDialogOpen(true)}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {parentObjective?.data.status === "draft" && (
            <KrCoachPanel
              objectiveStatement={parentObjective.data.title}
              krTitle={keyResult.title}
              krType={keyResult.type}
              krTargetValue={keyResult.target_value ?? null}
            />
          )}
          <KrProgressCard keyResult={keyResult} />
          {canCheckIn && <CheckInForm keyResult={keyResult} />}
          {!canCheckIn && canEdit && !isTerminalStatus(keyResult.status) && keyResult.status !== "in_progress" && (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Transition to In Progress to enable check-ins.
            </p>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent>
              <KeyResultInlineEditor keyResult={keyResult} canEdit={canEdit} />
            </CardContent>
          </Card>
          <ProgressUpdateList keyResult={keyResult} />
          <KudosList krId={keyResult.id} />
        </div>

        <div className="space-y-6">
          <KrMetadata keyResult={keyResult} year={parentObjective?.data.year} />
        </div>
      </div>

      <MoveKeyResultDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        keyResult={keyResult}
        sourceObjective={parentObjective?.data ?? null}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Key Result"
        description="Are you sure you want to delete this key result? This action cannot be undone."
        onConfirm={() => {
          deleteKeyResult.mutate(keyResult.id, {
            onSuccess: () => void navigate({ to: "/objectives/$id", params: { id: keyResult.objective_id } }),
          });
        }}
        isPending={deleteKeyResult.isPending}
      />

      <ConfirmRevertDialog
        open={revertDialogOpen}
        onOpenChange={setRevertDialogOpen}
        entityLabel="key result"
        onConfirm={() => {
          updateStatus.mutate("draft", {
            onSuccess: () => setRevertDialogOpen(false),
          });
        }}
        isPending={updateStatus.isPending}
      />
    </div>
  );
}
